// Secondary-market endpoints (investor-service `/api/marketplace`, contract
// v1). Each function returns the wire DTO — single resources unwrapped from
// the `{ data }` envelope, paginated lists as the full envelope so the parser
// can read the root `pagination`. Failures throw `AppError` (axios
// interceptor); nothing here catches.

import { investorApi } from '@core/api/httpClient'
import { TRADING_ENDPOINTS } from './tradingEndpointPaths'
import { requireData } from './tradingParser'
import type {
  BuyQuoteDto,
  BuyQuoteRequestDto,
  CreateListingRequestDto,
  EnvelopeDto,
  IdempotentRequestDto,
  MarketplaceListingDetailDto,
  MarketplaceListingDto,
  MarketplacePropertiesQueryDto,
  MarketplacePropertyCardDto,
  MarketplacePropertyDetailDto,
  MyListingDetailDto,
  MyListingDto,
  MyListingsQueryDto,
  PaginatedEnvelopeDto,
  PlaceSecondaryOrderRequestDto,
  SecondaryOrderDetailDto,
  SecondaryOrderSummaryDto,
  SecondaryOrdersQueryDto,
  SellOverviewDto,
  SellQuoteDto,
  SellQuoteRequestDto,
} from './tradingContracts'
import type {
  BuyQuoteRequest,
  CreateListingInput,
  IdempotentActionInput,
  MarketPropertyQuery,
  PlaceSecondaryOrderInput,
} from '../types/tradingTypes'

const withId = (path: string, key: string, id: string) =>
  path.replace(`{${key}}`, encodeURIComponent(id))

/** Prices go over the wire as decimal strings, never floats. */
const PRICE_DECIMALS = 2
const toDecimalString = (value: number): string => value.toFixed(PRICE_DECIMALS)

const joinList = (values: string[] | undefined): string | undefined =>
  values?.length ? values.join(',') : undefined

const toQueryNumber = (value: number | undefined): string | undefined =>
  value === undefined ? undefined : String(value)

// ── Browse ────────────────────────────────────────────────────────────────

const toPropertiesQuery = (
  query: MarketPropertyQuery,
  page: number,
  limit: number,
): MarketplacePropertiesQueryDto => ({
  search: query.search || undefined,
  propertyTypes: joinList(query.propertyTypes),
  locations: joinList(query.locations),
  priceMin: toQueryNumber(query.priceMin),
  priceMax: toQueryNumber(query.priceMax),
  tokensMin: toQueryNumber(query.tokensMin),
  tokensMax: toQueryNumber(query.tokensMax),
  varianceMin: toQueryNumber(query.varianceMin),
  varianceMax: toQueryNumber(query.varianceMax),
  ownedOnly: query.ownedOnly ? 'true' : undefined,
  sort: query.sort,
  page,
  limit,
})

export async function getMarketProperties(
  query: MarketPropertyQuery,
  page: number,
  limit: number,
): Promise<PaginatedEnvelopeDto<MarketplacePropertyCardDto>> {
  const { data } = await investorApi.get<PaginatedEnvelopeDto<MarketplacePropertyCardDto>>(
    TRADING_ENDPOINTS.PROPERTIES,
    { params: toPropertiesQuery(query, page, limit) },
  )
  return data
}

export async function getMarketProperty(propertyId: string): Promise<MarketplacePropertyDetailDto> {
  const { data } = await investorApi.get<EnvelopeDto<MarketplacePropertyDetailDto>>(
    withId(TRADING_ENDPOINTS.PROPERTY_DETAIL, 'propertyId', propertyId),
  )
  return requireData(data)
}

export async function getSellerListings(propertyId: string): Promise<MarketplaceListingDto[]> {
  const { data } = await investorApi.get<EnvelopeDto<MarketplaceListingDto[]>>(
    withId(TRADING_ENDPOINTS.PROPERTY_LISTINGS, 'propertyId', propertyId),
  )
  return data?.data ?? []
}

export async function getSellerListing(listingId: string): Promise<MarketplaceListingDetailDto> {
  const { data } = await investorApi.get<EnvelopeDto<MarketplaceListingDetailDto>>(
    withId(TRADING_ENDPOINTS.LISTING_DETAIL, 'listingId', listingId),
  )
  return requireData(data)
}

// ── Buy ───────────────────────────────────────────────────────────────────

export async function getBuyQuote(
  listingId: string,
  request: BuyQuoteRequest,
): Promise<BuyQuoteDto> {
  const body: BuyQuoteRequestDto =
    'amount' in request
      ? { amount: toDecimalString(request.amount) }
      : { tokenQuantity: request.tokenQuantity }
  const { data } = await investorApi.post<EnvelopeDto<BuyQuoteDto>>(
    withId(TRADING_ENDPOINTS.BUY_QUOTE, 'listingId', listingId),
    body,
  )
  return requireData(data)
}

export async function placeSecondaryOrder(
  input: PlaceSecondaryOrderInput,
): Promise<SecondaryOrderDetailDto> {
  const body: PlaceSecondaryOrderRequestDto = {
    listingId: input.listingId,
    tokenQuantity: input.tokens,
    termsAccepted: input.termsAccepted,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await investorApi.post<EnvelopeDto<SecondaryOrderDetailDto>>(
    TRADING_ENDPOINTS.ORDERS,
    body,
  )
  return requireData(data)
}

export async function getSecondaryOrders(
  query: SecondaryOrdersQueryDto,
): Promise<PaginatedEnvelopeDto<SecondaryOrderSummaryDto>> {
  const { data } = await investorApi.get<PaginatedEnvelopeDto<SecondaryOrderSummaryDto>>(
    TRADING_ENDPOINTS.ORDERS,
    { params: query },
  )
  return data
}

export async function getSecondaryOrder(orderId: string): Promise<SecondaryOrderDetailDto> {
  const { data } = await investorApi.get<EnvelopeDto<SecondaryOrderDetailDto>>(
    withId(TRADING_ENDPOINTS.ORDER_DETAIL, 'orderId', orderId),
  )
  return requireData(data)
}

export async function cancelSecondaryOrder({
  id,
  idempotencyKey,
}: IdempotentActionInput): Promise<SecondaryOrderDetailDto> {
  const body: IdempotentRequestDto = { idempotencyKey }
  const { data } = await investorApi.post<EnvelopeDto<SecondaryOrderDetailDto>>(
    withId(TRADING_ENDPOINTS.CANCEL_ORDER, 'orderId', id),
    body,
  )
  return requireData(data)
}

// ── Sell ──────────────────────────────────────────────────────────────────

export async function getSellOverview(propertyId: string): Promise<SellOverviewDto> {
  const { data } = await investorApi.get<EnvelopeDto<SellOverviewDto>>(
    withId(TRADING_ENDPOINTS.SELL_OVERVIEW, 'propertyId', propertyId),
  )
  return requireData(data)
}

export async function getSellQuote(
  propertyId: string,
  tokens: number,
  pricePerToken: number,
): Promise<SellQuoteDto> {
  const body: SellQuoteRequestDto = {
    tokenQuantity: tokens,
    pricePerToken: toDecimalString(pricePerToken),
  }
  const { data } = await investorApi.post<EnvelopeDto<SellQuoteDto>>(
    withId(TRADING_ENDPOINTS.SELL_QUOTE, 'propertyId', propertyId),
    body,
  )
  return requireData(data)
}

export async function createListing(input: CreateListingInput): Promise<MyListingDetailDto> {
  const body: CreateListingRequestDto = {
    propertyId: input.propertyId,
    tokenQuantity: input.tokens,
    pricePerToken: toDecimalString(input.pricePerToken),
    dividendNoticeAccepted: input.dividendNoticeAccepted,
    termsAccepted: input.termsAccepted,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await investorApi.post<EnvelopeDto<MyListingDetailDto>>(
    TRADING_ENDPOINTS.LISTINGS,
    body,
  )
  return requireData(data)
}

// ── My listings ───────────────────────────────────────────────────────────

export async function getMyListings(
  query: MyListingsQueryDto,
): Promise<PaginatedEnvelopeDto<MyListingDto>> {
  const { data } = await investorApi.get<PaginatedEnvelopeDto<MyListingDto>>(
    TRADING_ENDPOINTS.MY_LISTINGS,
    { params: query },
  )
  return data
}

export async function getMyListing(listingId: string): Promise<MyListingDetailDto> {
  const { data } = await investorApi.get<EnvelopeDto<MyListingDetailDto>>(
    withId(TRADING_ENDPOINTS.MY_LISTING_DETAIL, 'listingId', listingId),
  )
  return requireData(data)
}

export async function cancelListing({
  id,
  idempotencyKey,
}: IdempotentActionInput): Promise<MyListingDetailDto> {
  const body: IdempotentRequestDto = { idempotencyKey }
  const { data } = await investorApi.post<EnvelopeDto<MyListingDetailDto>>(
    withId(TRADING_ENDPOINTS.CANCEL_MY_LISTING, 'listingId', id),
    body,
  )
  return requireData(data)
}

export async function renewListing({
  id,
  idempotencyKey,
}: IdempotentActionInput): Promise<MyListingDetailDto> {
  const body: IdempotentRequestDto = { idempotencyKey }
  const { data } = await investorApi.post<EnvelopeDto<MyListingDetailDto>>(
    withId(TRADING_ENDPOINTS.RENEW_MY_LISTING, 'listingId', id),
    body,
  )
  return requireData(data)
}

// ── Full lists ────────────────────────────────────────────────────────────
// The Orders and Listings tabs search, filter and sort in memory (the API
// has no sort for them), so they read every page up front. An investor's own
// orders / listings are a short list; the page cap is a runaway guard.

const ALL_PAGES_LIMIT = 50
const MAX_PAGES = 20

async function collectPages<T>(
  fetchPage: (page: number) => Promise<PaginatedEnvelopeDto<T>>,
): Promise<T[]> {
  const rows: T[] = []
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await fetchPage(page)
    rows.push(...(response?.data ?? []))
    const totalPages = response?.pagination?.totalPages ?? 1
    if (page >= totalPages) break
  }
  return rows
}

export const getAllSecondaryOrders = (): Promise<SecondaryOrderSummaryDto[]> =>
  collectPages((page) => getSecondaryOrders({ page, limit: ALL_PAGES_LIMIT }))

export const getAllMyListings = (): Promise<MyListingDto[]> =>
  collectPages((page) => getMyListings({ page, limit: ALL_PAGES_LIMIT }))
